import React, { useEffect, useState, useMemo } from "react";
import { Phone, PhoneOff, Clock, Activity, ChevronDown } from "lucide-react";
import "./app.css";
import Loading from "./loading";
import { secretKey } from "./data";

const API = "https://apiftpsplit.ftpeople.pl";
const hdrs = { "Content-Type": "application/json", Authorization: secretKey };
const DEFAULT_VISIBLE = 7;

/* ── helpers ── */
const toDateKey = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(dt.getDate()).padStart(2, "0")}`;
};

const fmtTime = (totalMinutes) => {
  const h = Math.floor(totalMinutes / 60);
  const m = Math.round(totalMinutes % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};

const CALL_DURATION = { on: 5, off: 1 };

const processData = (raw) => {
  const dayMap = {};
  raw.forEach((r) => {
    const day = toDateKey(r.time);
    const hour = new Date(r.time).getHours();
    const isOn = r.type === "on";
    if (!dayMap[day]) dayMap[day] = { hours: {}, onTotal: 0, offTotal: 0 };
    if (!dayMap[day].hours[hour]) dayMap[day].hours[hour] = { on: 0, off: 0 };
    if (isOn) {
      dayMap[day].hours[hour].on++;
      dayMap[day].onTotal++;
    } else {
      dayMap[day].hours[hour].off++;
      dayMap[day].offTotal++;
    }
  });

  return Object.entries(dayMap)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, data]) => {
      const hoursArr = Object.entries(data.hours)
        .sort(([a], [b]) => +a - +b)
        .map(([h, c]) => ({
          hour: +h,
          on: c.on,
          off: c.off,
          workMin: c.on * CALL_DURATION.on + c.off * CALL_DURATION.off,
        }));
      return {
        day,
        activeHours: hoursArr.length,
        onTotal: data.onTotal,
        offTotal: data.offTotal,
        workMin:
          data.onTotal * CALL_DURATION.on + data.offTotal * CALL_DURATION.off,
        hours: hoursArr,
      };
    });
};

const calcTotals = (days) =>
  days.reduce(
    (a, d) => ({
      on: a.on + d.onTotal,
      off: a.off + d.offTotal,
      workMin: a.workMin + d.workMin,
      days: a.days + 1,
    }),
    { on: 0, off: 0, workMin: 0, days: 0 }
  );

/* ═══════════════════════════════════════════════════════════ */

const ClickReport = (props) => {
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState("");
  const [rawDetail, setRawDetail] = useState([]);
  const [expandedDay, setExpandedDay] = useState(null);
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE);

  /* ── fetch users ── */
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/getClicksReport`, {
        method: "POST",
        headers: hdrs,
        body: "{}",
      });
      const data = await res.json();
      const unique = [
        ...new Set(data.map((el) => el.user.replace(/ (SKD|CHF|WIBOR)/g, ""))),
      ];
      setUsers(unique);
      if (unique.length > 0) setUser(unique[0]);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  /* ── fetch detail ── */
  const fetchDetail = async (userName) => {
    const u = userName || user;
    if (!u) return;
    setLoadingDetail(true);
    setVisibleCount(DEFAULT_VISIBLE);
    setExpandedDay(null);
    try {
      const res = await fetch(`${API}/getClicksReportDetail`, {
        method: "POST",
        headers: hdrs,
        body: JSON.stringify({ user: u }),
      });
      setRawDetail(await res.json());
    } catch (err) {
      console.error(err);
    }
    setLoadingDetail(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  /* ── auto-fetch when user changes ── */
  useEffect(() => {
    if (!loading && user) fetchDetail(user);
  }, [user]);

  /* ── processed data ── */
  const allDays = useMemo(() => processData(rawDetail), [rawDetail]);
  const visibleDays = allDays.slice(0, visibleCount);
  const hasMore = allDays.length > visibleCount;

  const visibleTotals = useMemo(() => calcTotals(visibleDays), [visibleDays]);
  const allTotals = useMemo(() => calcTotals(allDays), [allDays]);

  if (loading) return <Loading />;

  return (
    <div className="cr-wrapper">
      {/* ── User select ── */}
      <div className="cr-toolbar">
        <div className="cr-select-group">
          <label className="db-field-label">Handlowiec</label>
          <select
            className="db-input db-select"
            value={user}
            onChange={(e) => setUser(e.target.value)}
          >
            {users.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        {allDays.length > 0 && (
          <span className="cr-range-info">
            {allDays.length} dni danych · wyświetlane:{" "}
            {Math.min(visibleCount, allDays.length)}
          </span>
        )}
      </div>

      {loadingDetail && <Loading />}

      {!loadingDetail && allDays.length > 0 && (
        <>
          {/* ── Summary stats ── */}
          <div className="cr-stats">
            <div className="cr-stat">
              <div className="cr-stat-icon cr-stat-icon--on">
                <Phone size={18} />
              </div>
              <div>
                <span className="cr-stat-num">{visibleTotals.on}</span>
                <span className="cr-stat-label">odebranych</span>
              </div>
            </div>
            <div className="cr-stat">
              <div className="cr-stat-icon cr-stat-icon--off">
                <PhoneOff size={18} />
              </div>
              <div>
                <span className="cr-stat-num">{visibleTotals.off}</span>
                <span className="cr-stat-label">nieodebranych</span>
              </div>
            </div>
            <div className="cr-stat">
              <div className="cr-stat-icon cr-stat-icon--time">
                <Clock size={18} />
              </div>
              <div>
                <span className="cr-stat-num">
                  {fmtTime(visibleTotals.workMin)}
                </span>
                <span className="cr-stat-label">przepracowano</span>
              </div>
            </div>
            <div className="cr-stat">
              <div className="cr-stat-icon cr-stat-icon--days">
                <Activity size={18} />
              </div>
              <div>
                <span className="cr-stat-num">{visibleTotals.days}</span>
                <span className="cr-stat-label">dni aktywności</span>
              </div>
            </div>
          </div>

          {/* ── Day cards ── */}
          <div className="cr-days">
            {visibleDays.map((d) => {
              const isOpen = expandedDay === d.day;
              return (
                <div
                  key={d.day}
                  className={`cr-day ${isOpen ? "cr-day--open" : ""}`}
                >
                  <button
                    className="cr-day-header"
                    onClick={() => setExpandedDay(isOpen ? null : d.day)}
                  >
                    <span className="cr-day-date">{d.day}</span>
                    <div className="cr-day-summary">
                      <span className="cr-day-pill cr-day-pill--on">
                        <Phone size={12} /> {d.onTotal}
                      </span>
                      <span className="cr-day-pill cr-day-pill--off">
                        <PhoneOff size={12} /> {d.offTotal}
                      </span>
                      <span className="cr-day-pill cr-day-pill--time">
                        <Clock size={12} /> {fmtTime(d.workMin)}
                      </span>
                      <span className="cr-day-pill cr-day-pill--hours">
                        {d.activeHours}h aktywności
                      </span>
                    </div>
                    <span className="cr-day-chevron">{isOpen ? "▲" : "▼"}</span>
                  </button>

                  {isOpen && (
                    <div className="cr-hours">
                      <div className="cr-hours-header">
                        <span>Godzina</span>
                        <span>Odebrane</span>
                        <span>Nieodebrane</span>
                        <span>Czas</span>
                      </div>
                      {d.hours.map((h) => (
                        <div key={h.hour} className="cr-hour-row">
                          <span className="cr-hour-label">
                            {h.hour}:00 – {h.hour + 1}:00
                          </span>
                          <span className="cr-hour-on">{h.on}</span>
                          <span className="cr-hour-off">{h.off}</span>
                          <span className="cr-hour-time">
                            {fmtTime(h.workMin)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Show more / less ── */}
          {(hasMore || visibleCount > DEFAULT_VISIBLE) && (
            <div className="cr-show-more">
              {hasMore && (
                <button
                  className="lq-btn lq-btn--muted cr-more-btn"
                  onClick={() => setVisibleCount(visibleCount + 7)}
                >
                  <ChevronDown size={15} /> Pokaż kolejne 7 dni (
                  {allDays.length - visibleCount} pozostało)
                </button>
              )}
              {visibleCount > DEFAULT_VISIBLE && (
                <button
                  className="lq-btn lq-btn--muted cr-more-btn"
                  onClick={() => setVisibleCount(DEFAULT_VISIBLE)}
                >
                  Pokaż tylko ostatnie 7 dni
                </button>
              )}
              {hasMore && (
                <button
                  className="lq-btn lq-btn--muted cr-more-btn"
                  onClick={() => setVisibleCount(allDays.length)}
                >
                  Pokaż wszystkie ({allDays.length})
                </button>
              )}
            </div>
          )}

          {/* ── All-time totals when partial ── */}
          {visibleCount < allDays.length && (
            <div className="cr-all-totals">
              Łącznie za {allDays.length} dni: {allTotals.on} odb. /{" "}
              {allTotals.off} nieodb. / {fmtTime(allTotals.workMin)}{" "}
              przepracowano
            </div>
          )}
        </>
      )}

      {!loadingDetail &&
        allDays.length === 0 &&
        rawDetail.length === 0 &&
        user && (
          <div className="cr-empty">
            <Activity size={32} strokeWidth={1} />
            <p>Brak danych dla wybranego handlowca</p>
          </div>
        )}
    </div>
  );
};

export default ClickReport;
